import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, InputGroup, InputGroupAddon } from "reactstrap";
import { Link } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import * as Loading from "@repoflow/components/loading";
import * as OrganizationQL from "queries/organization";
import * as Section from "@repoflow/components/section";

// Search on SUBMIT, not on every keystroke: type freely, press Enter or hit the
// button, and the box holds with a spinner until the results land. This matches
// the buckets box in bucketws, which is the other half of the same contract —
// search-as-you-type is a real option now that a query costs a couple of hundred
// milliseconds, but it is a change to how both boxes behave and worth making on
// its own.
//
// The domain is a prop rather than a context read: the query hangs off
// domains.get(domain:), so it is a query variable here in a way it is not for
// buckets.
export const SearchBox = ({ domain, onResults }) => {
  const [term, setTerm] = useState("");
  const inputRef = useRef(null);

  const [runSearch, { loading }] = useLazyQuery(OrganizationQL.search, {
    fetchPolicy: "no-cache",
    onCompleted: (data) =>
      onResults(
        data?.viewer?.account?.domains?.get?.organizations?.search?.items ?? []
      ),
    // A failed search must not leave the box disabled forever.
    onError: () => onResults([]),
  });

  const disabled = loading;

  // Disabling the input drops focus, so hand it back when it re-enables after a
  // search. Without this the user has to click again to type their next query.
  //
  // Gated on the disabled -> enabled transition rather than on `!disabled`: the
  // box is enabled on mount too, and the plain check steals the caret on page
  // load from a user who never asked for it. StrictMode double-invokes this
  // effect, but both passes store the same value, so the write is safe.
  const prevDisabledRef = useRef(false);
  useEffect(() => {
    if (prevDisabledRef.current && !disabled && inputRef.current) {
      inputRef.current.focus();
    }
    prevDisabledRef.current = disabled;
  }, [disabled]);

  const onSubmit = (event) => {
    event.preventDefault();
    const query = term.trim();

    // An empty box is not a search — it is "show me the ordinary list again".
    if (!query) {
      onResults(null);
      return;
    }

    runSearch({ variables: { domain, input: { query, limit: 5 } } });
  };

  return (
    <Form onSubmit={onSubmit}>
      <InputGroup>
        <Input
          innerRef={inputRef}
          value={term}
          disabled={disabled}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={loading ? "Searching…" : "Search organizations"}
          aria-busy={disabled}
        />
        <InputGroupAddon addonType="append">
          <Button color="primary" type="submit" disabled={disabled}>
            {disabled ? <Loading.Icon /> : <i className="fa fa-search" />}
          </Button>
        </InputGroupAddon>
      </InputGroup>
    </Form>
  );
};

// Results are rendered here rather than through @repoflow/components/list:
// that component folds orderBy/orderMode into its `input`, and SearchInput takes
// neither — results come back in score order.
export const SearchResults = ({ items }) => {
  const { base } = Section.useSectionContext();

  if (items.length === 0) {
    return <Alert color="light">No organizations match that search.</Alert>;
  }

  return (
    <table className="table">
      <tbody>
        {items.map(({ orgId, description }) => (
          <tr key={orgId}>
            <td>{description}</td>
            <td className="text-muted">{orgId}</td>
            <td className="text-right">
              <Link className="btn btn-primary btn-sm" to={`${base}/${orgId}`}>
                View
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
